import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { rotateWorldPoint } from '../utils/common_constants.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';
import { getPlanSvg, screenToWorld, setStatus } from '../render/plan_renderer.js?v=20260904f';
import * as SelectionService from '../selection/selection_service.js?v=20260904f';
import {
  SKETCH_WALL_SYSTEM_ID,
  allSketchNodes,
  allSketchWalls,
  clearSketchWallSelection,
  deleteSketchWall,
  ensureSketchWallState,
  normalizeSketchWallStoredAngles,
  setSketchWallSelectionIds,
  sketchNodeById,
  sketchWallById,
  sketchWallConnectedNodeIds,
  sketchWallCorners,
  sketchWallAbsoluteAngle,
  sketchWallFurring,
  sketchWallFurringQuad
} from './sketch_wall_state.js?v=20260904f';
import { solveSketchWalls } from './sketch_wall_solver.js?v=20260706a';

function sketchWallPoint(nodeId) {
  const node = sketchNodeById(nodeId);
  return node ? { x: node.x, z: node.z } : { x: 0, z: 0 };
}

function sketchWallPolygons(wall) {
  if (!wall) return [];
  const corners = sketchWallCorners(wall, sketchWallPoint);
  const polygons = [[corners.startInner, corners.endInner, corners.endOuter, corners.startOuter]];
  if (sketchWallFurring(wall).enabled) {
    polygons.push(sketchWallFurringQuad(wall, sketchWallPoint, corners));
  }
  return polygons;
}

function pointInRect(point, rect) {
  return point.x >= rect.minX && point.x <= rect.maxX
    && point.z >= rect.minZ && point.z <= rect.maxZ;
}

function pointOnSegment(point, start, end) {
  const cross = (point.z - start.z) * (end.x - start.x) - (point.x - start.x) * (end.z - start.z);
  if (Math.abs(cross) > 0.001) return false;
  return point.x >= Math.min(start.x, end.x) - 0.001
    && point.x <= Math.max(start.x, end.x) + 0.001
    && point.z >= Math.min(start.z, end.z) - 0.001
    && point.z <= Math.max(start.z, end.z) + 0.001;
}

function pointInPolygon(point, polygon) {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const a = polygon[previous];
    const b = polygon[index];
    if (pointOnSegment(point, a, b)) return true;
    const crosses = ((a.z > point.z) !== (b.z > point.z))
      && point.x < ((b.x - a.x) * (point.z - a.z)) / (b.z - a.z) + a.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

function orientation(a, b, c) {
  return (b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x);
}

function segmentsIntersect(a, b, c, d) {
  const abC = orientation(a, b, c);
  const abD = orientation(a, b, d);
  const cdA = orientation(c, d, a);
  const cdB = orientation(c, d, b);
  if (((abC > 0 && abD < 0) || (abC < 0 && abD > 0))
    && ((cdA > 0 && cdB < 0) || (cdA < 0 && cdB > 0))) return true;
  return Math.abs(abC) <= 0.001 && pointOnSegment(c, a, b)
    || Math.abs(abD) <= 0.001 && pointOnSegment(d, a, b)
    || Math.abs(cdA) <= 0.001 && pointOnSegment(a, c, d)
    || Math.abs(cdB) <= 0.001 && pointOnSegment(b, c, d);
}

function rectCorners(rect) {
  return [
    { x: rect.minX, z: rect.minZ },
    { x: rect.maxX, z: rect.minZ },
    { x: rect.maxX, z: rect.maxZ },
    { x: rect.minX, z: rect.maxZ }
  ];
}

function polygonIntersectsRect(polygon, rect) {
  if (polygon.some(point => pointInRect(point, rect))) return true;
  const corners = rectCorners(rect);
  if (corners.some(point => pointInPolygon(point, polygon))) return true;
  for (let polygonIndex = 0; polygonIndex < polygon.length; polygonIndex += 1) {
    const polygonStart = polygon[polygonIndex];
    const polygonEnd = polygon[(polygonIndex + 1) % polygon.length];
    for (let rectIndex = 0; rectIndex < corners.length; rectIndex += 1) {
      if (segmentsIntersect(polygonStart, polygonEnd, corners[rectIndex], corners[(rectIndex + 1) % corners.length])) {
        return true;
      }
    }
  }
  return false;
}

function sketchWallMatchesRect(wall, rect, mode = 'window') {
  const polygons = sketchWallPolygons(wall);
  if (mode === 'crossing') return polygons.some(polygon => polygonIntersectsRect(polygon, rect));
  return polygons.length > 0 && polygons.every(polygon => polygon.every(point => pointInRect(point, rect)));
}

function collectSketchWallBoxSelection(rect, mode = 'window') {
  ensureSketchWallState();
  return {
    sketchWallIds: allSketchWalls()
      .filter(wall => sketchWallMatchesRect(wall, rect, mode))
      .map(wall => wall.id)
  };
}

function hitTestSketchWall(clientX, clientY) {
  const svg = getPlanSvg();
  if (!svg) return null;
  const rect = svg.getBoundingClientRect();
  const point = screenToWorld(clientX - rect.left, clientY - rect.top);
  for (const wall of [...allSketchWalls()].reverse()) {
    if (sketchWallPolygons(wall).some(polygon => pointInPolygon(point, polygon))) {
      return { kind: 'sketch-wall', id: wall.id, wall, point };
    }
  }
  return null;
}

function createSketchWallMoveSnapshot(ids = []) {
  const wallIds = [...new Set(ids)].filter(id => sketchWallById(id));
  const nodeIds = new Set();
  for (const wallId of wallIds) {
    const wall = sketchWallById(wallId);
    if (!wall) continue;
    for (const nodeId of sketchWallConnectedNodeIds(wall)) nodeIds.add(nodeId);
  }
  const nodes = allSketchNodes()
    .filter(node => nodeIds.has(node.id))
    .map(node => ({ id: node.id, x: node.x, z: node.z }));
  return wallIds.length ? { wallIds, nodes, count: wallIds.length } : null;
}

function applySketchWallMoveSnapshot(snapshot, dx = 0, dz = 0) {
  for (const entry of snapshot?.nodes || []) {
    const node = sketchNodeById(entry.id);
    if (!node) continue;
    node.x = entry.x + dx;
    node.z = entry.z + dz;
  }
  return snapshot?.wallIds?.length || 0;
}

function selectConnectedSketchWalls(wallId) {
  const wall = sketchWallById(wallId);
  if (!wall) return false;
  const nodes = sketchWallConnectedNodeIds(wall);
  SelectionService.clearSystemSelectionsExcept(SKETCH_WALL_SYSTEM_ID);
  setSketchWallSelectionIds(allSketchWalls().filter(w => nodes.has(w.startNodeId)).map(w => w.id));
  AppRuntime.requestAppRefresh(false, 'sketch-wall-connected-select');
  return true;
}

function createSketchWallRotationSnapshot(ids = []) {
  if (!ids.length) return null;
  const move = createSketchWallMoveSnapshot(ids);
  if (!move) return null;
  const nodeIds = new Set(move.nodes.map(n => n.id));
  const walls = allSketchWalls().filter(w => nodeIds.has(w.startNodeId));
  return {
    ...move,
    points: walls.flatMap(w => sketchWallPolygons(w).flat()),
    rotation: sketchWallAbsoluteAngle(walls[0]) * 180 / Math.PI,
    walls: walls.map(w => ({ id: w.id, fixed: w.fixed, fixedAngle: w.fixedAngle,
      fixedSource: w.fixedSource, horizontal: w.horizontal, vertical: w.vertical,
      direction: sketchWallAbsoluteAngle(w) }))
  };
}

function applySketchWallRotationSnapshot(snapshot, angleDeg, pivot) {
  if (!snapshot || !pivot) return 0;
  for (const saved of snapshot.nodes) {
    const node = sketchNodeById(saved.id);
    if (node) Object.assign(node, rotateWorldPoint(saved, pivot, angleDeg));
  }
  for (const saved of snapshot.walls) {
    const wall = sketchWallById(saved.id);
    if (!wall) continue;
    const { direction, ...original } = saved;
    Object.assign(wall, original);
    if (Math.abs(angleDeg) < 1e-10) continue;
    // Absolute axis/direction constraints rotate with the rigid component.
    // Relative angles and authored inner/outer lengths remain untouched.
    if (saved.horizontal || saved.vertical || (saved.fixed && Number.isFinite(saved.fixedAngle))) {
      const heading = (Number.isFinite(saved.fixedAngle) ? saved.fixedAngle : direction) + angleDeg * Math.PI / 180;
      wall.horizontal = Boolean(saved.horizontal || saved.vertical) && Math.abs(Math.sin(heading)) < 1e-9;
      wall.vertical = Boolean(saved.horizontal || saved.vertical) && Math.abs(Math.cos(heading)) < 1e-9;
      wall.fixed = true;
      wall.fixedAngle = heading;
    }
  }
  return snapshot.walls.length;
}

function deleteSketchWalls(ids = []) {
  let deleted = 0;
  for (const id of [...new Set(ids)]) {
    if (deleteSketchWall(id)) deleted += 1;
  }
  if (deleted) solveSketchWalls();
  return deleted;
}

function sketchWallSelectionSystem() {
  return {
    id: SKETCH_WALL_SYSTEM_ID,
    categoryId: '__core_hidden__',
    companyId: '__core__',
    label: 'Parametrische Wände',
    meta: { globalSystem: true, hiddenFromNavigation: true, omitFromBom: true },
    multiSelectionFields: ['sketchWallIds'],
    selectionPriority: 840,
    resolveItemToMultiSelection(item) {
      return item?.kind === 'wall' && item.id ? { field: 'sketchWallIds', id: item.id } : null;
    },
    resolveHitToMultiSelection(hit) {
      const id = hit?.wall?.id || hit?.id;
      return id ? { field: 'sketchWallIds', id } : null;
    },
    resolveHitToItems(hit) {
      const id = hit?.wall?.id || hit?.id;
      return id ? { owner: SKETCH_WALL_SYSTEM_ID, kind: 'wall', id } : null;
    },
    mergeLegacySelection(resolved, selection) {
      const ids = selection?.sketchWallIds?.length
        ? selection.sketchWallIds
        : selection?.sketchWall?.wallId
          ? [selection.sketchWall.wallId]
          : [];
      resolved.sketchWallIds.push(...ids);
    },
    syncLegacySelectionFields(selection) {
      setSketchWallSelectionIds(selection?.sketchWallIds || []);
    },
    clearSelection: clearSketchWallSelection,
    hitTest2D: hitTestSketchWall,
    ownsDomTarget(target) {
      return Boolean(target?.closest?.('[data-sketch-wall-id]'));
    },
    handleClick(ctx = {}) {
      const id = ctx.hit?.wall?.id || ctx.hit?.id;
      if (!id) return false;
      SelectionService.clearSystemSelectionsExcept(SKETCH_WALL_SYSTEM_ID);
      setSketchWallSelectionIds([id]);
      setStatus('Parametrische Wand markiert');
      return true;
    },
    handleDoubleClick(ctx = {}) {
      if (ctx.event?.button !== 0 || S.ui?.activeTool) return false;
      return selectConnectedSketchWalls(ctx.hit?.wall?.id || ctx.hit?.id);
    },
    hitBelongsToSelection(hit, selection = {}) {
      const id = hit?.wall?.id || hit?.id;
      return Boolean(id && (selection.sketchWallIds || []).includes(id));
    },
    createMoveSnapshot(ctx = {}) {
      return createSketchWallMoveSnapshot(ctx.selection?.sketchWallIds || []);
    },
    applyMoveSnapshot(ctx = {}) {
      return applySketchWallMoveSnapshot(
        ctx.snapshot,
        ctx.snappedDx ?? ctx.dx ?? 0,
        ctx.snappedDz ?? ctx.dz ?? 0
      );
    },
    syncMoveSelection(ctx = {}) {
      solveSketchWalls();
      return { sketchWallIds: ctx.snapshot?.wallIds || [] };
    },
    deleteObjects(ctx = {}) {
      return deleteSketchWalls(ctx.selection?.sketchWallIds || []);
    },
    collectBoxSelection: collectSketchWallBoxSelection,
    selectBoxSelection(ctx = {}) {
      const ids = ctx.selection?.sketchWallIds || [];
      if (!ids.length) return false;
      setSketchWallSelectionIds(ctx.multiple ? ids : [ids[0]]);
      return true;
    }
  };
}

function registerCoreSketchWallSelectionSystem(registry, projectStateService) {
  projectStateService?.registerProjectNormalizer?.(SKETCH_WALL_SYSTEM_ID, () => {
    ensureSketchWallState();
    if (normalizeSketchWallStoredAngles()) solveSketchWalls();
    setSketchWallSelectionIds(S.selection?.sketchWallIds || []);
  }, { priority: 125 });
  SelectionService.registerLegacySelectionHandler(SKETCH_WALL_SYSTEM_ID, item => {
    if (item?.kind !== 'wall' || !sketchWallById(item.id)) return false;
    setSketchWallSelectionIds([item.id]);
    return true;
  });
  SelectionService.registerLegacySelectionClearHandler(SKETCH_WALL_SYSTEM_ID, () => {
    clearSketchWallSelection();
    return true;
  });
  SelectionService.registerLegacySelectionCollector(SKETCH_WALL_SYSTEM_ID, {
    priority: 490,
    collectMulti(legacy) {
      const ids = legacy?.sketchWallIds?.length
        ? legacy.sketchWallIds
        : legacy?.sketchWall?.wallId
          ? [legacy.sketchWall.wallId]
          : [];
      return ids.map(id => ({ owner: SKETCH_WALL_SYSTEM_ID, kind: 'wall', id }));
    }
  });
  registry.registerSystem(sketchWallSelectionSystem());
}

export {
  selectConnectedSketchWalls,
  createSketchWallRotationSnapshot,
  applySketchWallRotationSnapshot,
  applySketchWallMoveSnapshot,
  collectSketchWallBoxSelection,
  createSketchWallMoveSnapshot,
  hitTestSketchWall,
  pointInPolygon,
  registerCoreSketchWallSelectionSystem,
  sketchWallMatchesRect,
  sketchWallSelectionSystem
};
